<template>
    <div class="cursos-home">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><a href="javascript:;">Dashboard</a></li>
                                <li class="breadcrumb-item active" aria-current="page"><span>Cursos</span></li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <CursosHeroBanner
            v-model="searchQuery"
            eyebrow="Sua evolução começa aqui"
            title="Aprenda no seu ritmo"
            subtitle="Explore os cursos disponíveis e desenvolva suas habilidades com conteúdos de qualidade, direto ao ponto e com quem entende do assunto."
            search-placeholder="Buscar cursos..."
            search-aria-label="Buscar cursos"
        />

        <section class="cursos-catalog container" aria-labelledby="cursos-catalog-title">
            <div class="cursos-catalog-header">
                <div class="cursos-catalog-heading">
                    <h2 id="cursos-catalog-title" class="cursos-catalog-title">Nossos cursos</h2>
                    <p class="cursos-catalog-subtitle">
                        Cursos completos, práticos e focados no que realmente importa.
                    </p>
                </div>

                <div class="cursos-filters" role="tablist" aria-label="Filtrar cursos por categoria">
                    <button
                        v-for="filtro in filtros"
                        :key="filtro.id"
                        type="button"
                        role="tab"
                        class="cursos-filter-btn"
                        :class="{ active: filtroAtivo === filtro.id }"
                        :aria-selected="filtroAtivo === filtro.id"
                        @click="filtroAtivo = filtro.id"
                    >
                        {{ filtro.label }}
                    </button>
                </div>
            </div>

            <div v-if="cursoStore.loading" class="row cursos-catalog-skeleton">
                <div
                    v-for="n in 8"
                    :key="n"
                    class="col-xl-3 col-lg-4 col-md-6"
                >
                    <div class="skeleton-card">
                        <div class="skeleton-card-image skeleton"></div>
                        <div class="skeleton-card-body">
                            <div class="skeleton skeleton-category"></div>
                            <div class="skeleton skeleton-title"></div>
                            <div class="skeleton skeleton-text"></div>
                            <div class="skeleton skeleton-text short"></div>
                            <div class="skeleton-meta">
                                <span class="skeleton skeleton-meta-item"></span>
                                <span class="skeleton skeleton-meta-item"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="cursoStore.error" class="cursos-state text-center py-5">
                <div class="alert alert-danger" role="alert">
                    <strong>Erro!</strong> {{ cursoStore.error }}
                </div>
                <button class="btn btn-primary mt-3" type="button" @click="recarregar">
                    Tentar Novamente
                </button>
            </div>

            <div v-else-if="cursosFiltrados.length === 0" class="cursos-state text-center py-5">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-muted mb-3"
                    style="opacity: 0.5;"
                >
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                <p class="text-muted mb-0">Nenhum curso encontrado.</p>
            </div>

            <div v-else class="row">
                <div
                    v-for="curso in cursosFiltrados"
                    :key="curso.id"
                    class="col-xl-3 col-lg-4 col-md-6"
                >
                    <article
                        class="curso-card"
                        role="link"
                        tabindex="0"
                        @click="navigateToCurso(curso.id)"
                        @keydown.enter.prevent="navigateToCurso(curso.id)"
                        @keydown.space.prevent="navigateToCurso(curso.id)"
                    >
                        <div class="curso-image-wrapper">
                            <img
                                :src="curso.imagem"
                                :alt="curso.titulo"
                                class="curso-card-img"
                                @error="handleImageError($event)"
                            />
                            <span v-if="curso.destaque" class="curso-badge-destaque">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                </svg>
                                Destaque
                            </span>
                            <span class="curso-badge-tipo">Curso</span>
                            <div v-if="curso.progresso > 0" class="curso-progress-overlay">
                                <div class="progress progress-sm">
                                    <div
                                        class="progress-bar"
                                        :style="{ width: curso.progresso + '%' }"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div class="curso-card-body">
                            <span class="curso-category">{{ categoriaCursoLabel(curso) }}</span>
                            <h3 class="curso-card-title">{{ curso.titulo }}</h3>

                            <div v-if="curso.avaliacao > 0" class="curso-rating">
                                <span class="curso-rating-stars" aria-hidden="true">
                                    <svg
                                        v-for="i in 5"
                                        :key="i"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        :class="{ checked: i <= Math.round(curso.avaliacao) }"
                                    >
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                    </svg>
                                </span>
                                <span class="curso-rating-score">{{ curso.avaliacao.toFixed(1) }}</span>
                                <span v-if="curso.totalAvaliacoes > 0" class="curso-rating-count">
                                    ({{ curso.totalAvaliacoes }} {{ curso.totalAvaliacoes === 1 ? 'avaliação' : 'avaliações' }})
                                </span>
                            </div>

                            <p class="curso-card-text">{{ truncarDescricao(curso.descricao, 110) }}</p>

                            <div class="curso-card-footer">
                                <div class="curso-meta">
                                    <span class="meta-item">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        {{ curso.totalAulas }} aulas
                                    </span>
                                    <span class="meta-item">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        {{ curso.duracao }}
                                    </span>
                                </div>
                                <span class="curso-card-arrow" aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                        <polyline points="12 5 19 12 12 19"></polyline>
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMeta } from '/src/composables/use-meta';
import { useCursoStore } from '@/stores/curso';
import { DEFAULT_COURSE_IMAGE_URL, applyImageFallbackOnce } from '@/utils/imageFallback';
import {
    truncarDescricao,
    categoriaCursoLabel,
    normalizarTextoBusca,
} from '@/utils/cursoCatalog';
import CursosHeroBanner from '@/components/cursos/CursosHeroBanner.vue';
import '@/assets/sass/pages/cursos-vitrine.scss';

useMeta({ title: 'Tela inicial' });

const router = useRouter();
const cursoStore = useCursoStore();
const searchTimeout = ref(null);
const filtroAtivo = ref('todos');

const filtros = [
    { id: 'todos', label: 'Todos' },
    { id: 'enem', label: 'ENEM', keywords: ['enem'] },
    { id: 'redacao', label: 'Redação', keywords: ['redacao'] },
    { id: 'simulados', label: 'Simulados', keywords: ['simulado'] },
    { id: 'videoaulas', label: 'Vídeo aulas', keywords: ['video', 'videoaula'] },
];

const searchQuery = computed({
    get: () => cursoStore.headerSearchQuery,
    set: (value) => cursoStore.setHeaderSearchQuery(value),
});

const cursoCombinaFiltro = (curso, filtro) => {
    if (!filtro || filtro.id === 'todos') return true;
    const texto = normalizarTextoBusca(
        curso.titulo,
        curso.descricao,
        curso.target_audience,
        curso.nivel
    );
    return filtro.keywords.some((keyword) => texto.includes(keyword));
};

const cursosFiltrados = computed(() => {
    const filtro = filtros.find((item) => item.id === filtroAtivo.value);
    return cursoStore.cursos.filter((curso) => cursoCombinaFiltro(curso, filtro));
});

const carregarCursos = async () => {
    await cursoStore.fetchCursos({
        search: cursoStore.headerSearchQuery,
        page: 1,
    });
};

const recarregar = () => {
    carregarCursos().catch(() => {});
};

watch(() => cursoStore.headerSearchQuery, () => {
    if (searchTimeout.value) clearTimeout(searchTimeout.value);
    searchTimeout.value = setTimeout(() => {
        carregarCursos().catch(() => {});
    }, 400);
});

onMounted(() => {
    carregarCursos().catch(() => {});
});

const navigateToCurso = (cursoId) => {
    router.push(`/curso/${cursoId}`);
};

const handleImageError = (event) => {
    applyImageFallbackOnce(event, DEFAULT_COURSE_IMAGE_URL);
};
</script>
